import { useRef } from "react";
import { toast } from "sonner";

import type { PartnerDetailDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useUpdatePartnerMutation } from "Partners/Api/Mutations/useUpdatePartnerMutation";
import {
  PartnerForm,
  type PartnerFormHandle,
} from "Partners/Forms/PartnerForm";
import {
  type PartnerFormData,
  toSavePartnerRequest,
} from "Partners/Forms/partnerFormSchema";
import { BlModal } from "Shared/Dialogs/BlModal";
import type { BlOption } from "Shared/Inputs/BlOption";

interface EditPartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  partner: PartnerDetailDto;
  partnerID: string;
  superiorPartnerOptions: BlOption[];
  lockedParentPartnerID?: string;
}

export const EditPartnerModal = ({
  isOpen,
  onClose,
  partner,
  partnerID,
  superiorPartnerOptions,
  lockedParentPartnerID,
}: EditPartnerModalProps): React.ReactNode => {
  const { t } = useResource();
  const formRef = useRef<PartnerFormHandle | null>(null);
  const updatePartnerMutation = useUpdatePartnerMutation();

  const onSubmit = async (formData: PartnerFormData) => {
    try {
      await updatePartnerMutation.mutateAsync({
        partnerID,
        data: toSavePartnerRequest(formData),
      });
      toast.success(t(Resources.Partners.EditPartnerModal.SuccessMessage));
      onClose();
    } catch {
      // field errors shown in form, non-field errors as toast via useHandleServerError
    }
  };

  const handleClose = () => {
    formRef.current?.reset();
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Partners.EditPartnerModal.Title)}
      onCancel={handleClose}
      onConfirm={() => formRef.current?.submit(onSubmit)()}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Partners.EditPartnerModal.Save)}
      maxWidth="800px"
    >
      <PartnerForm
        partner={partner}
        superiorPartnerOptions={superiorPartnerOptions}
        formRef={formRef}
        serverError={updatePartnerMutation.error}
        lockedParentPartnerID={lockedParentPartnerID}
      />
    </BlModal>
  );
};
